import React, { useState, useEffect } from "react"
import { Formik, Field, Form } from "formik"
import fields from "models/arcLot"
import fieldStatus from "helpers/fieldStatus"
import * as Yup from "yup"
import { useSelector, useDispatch } from "react-redux"
import { loadingStatus, getPayload } from "actions"
import Typeahead from "components/Typeahead"
import TextArea from "components/TextArea"
import classnames from "classnames"
import superagent from "superagent"
import settings from "settings"
import NumberField from "components/NumberField"
import _ from "lodash"
import ReactSelect from "components/ReactSelect"
import axios from "axios"
import currentUser from "helpers/currentUser"
import ArcRepetitionMap from "containers/mapping/arcRepetitionMap"
import DateField from "components/DateField"
import submitDataAsync from "helpers/asyncSubmitData"
import helpers from "helpers/helpers"
import { toast } from "react-toastify"

const ArcModal = (props) => {
	let arcFields = fields
	if (
		settings?.arcSitePlanFiles &&
		settings?.arcSitePlanFiles[props?.data?.property] &&
		settings.arcSitePlanFiles[props.data.property].length
	) {
		_.forEach(settings.arcSitePlanFiles[props.data.property], (v) => {
			if (v.required) {
				arcFields[v.name] = ""
			}
		})
	}
	if (settings?.arcCustomLotFields) {
		_.forEach(settings.arcCustomLotFields, (v, k) => {
			if (v.properties.includes("*") || v.properties.includes(props.data.property)) {
				arcFields[v.dbName] = ""
			}
		})
	}
	const [state, setState] = useState({
		fields: arcFields,
		error: false,
		floorplan: false,
		elevation: false,
		noticeStatus: false,
		user: currentUser(),
		current: {},
		noticeDropStatus: false,
		files: {
			notice: [],
		},
		data: {
			address: "",
		},
	})
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [blocking, setBlocking] = useState(false)

	const [geo, setGeo] = useState(null)

	const dispatch = useDispatch()
	const { payload } = useSelector((state) => ({
		payload: state.payload,
	}))

	useEffect(() => {
		if (_.has(props.data, `arc_data`) && props.data.arc_data) {
			let d = props.data.arc_data
			let updatedFields = _.clone(state.fields)
			let fp = _.find(payload.public.floorplans, { id: d.floorplan })
			updatedFields.arc_options = d.arc_options
			updatedFields.brick = d.brick
			updatedFields.elevation = d.elevation
			updatedFields.garage_color = d.garage_color
			updatedFields.roof_color = d.roof_color
			updatedFields.siding = d.siding
			updatedFields.siding_type = d.siding_type
			updatedFields.stone = d.stone
			updatedFields.stucco = d.stucco
			updatedFields.accent_color = d.accent_color
			updatedFields.trim = d.trim
			updatedFields.floorplan = d.floorplan
			updatedFields.floorplan_label = fp ? fp.name : ""
			updatedFields.approval_comments = d.approval_comments
			updatedFields.comments = d.comments
			updatedFields.sqft = d.sqft
			updatedFields.roof_pitch = d.roof_pitch
			updatedFields.total_height = d.total_height
			updatedFields.driveway_position = d.driveway_position
			updatedFields.masonry_percent = d.masonry_percent
			updatedFields.door_color = d.door_color
			updatedFields.brick_material = d.brick_material
			updatedFields.variance = d.variance
			/// about to get on the cbas flight, so scary, but shouldn't this just be {...d}  with the floorplan_label?
			// Go through custom lot fields and add
			if (settings?.arcCustomLotFields) {
				_.forEach(settings.arcCustomLotFields, (v, k) => {
					if (v.properties.includes("*") || v.properties.includes(props.data.property)) {
						updatedFields[v.dbName] = d[v.dbName]
					}
				})
			}

			setState((prevState) => ({ ...prevState, fields: updatedFields, current: d }))
		}
	}, [props.data, payload.public.floorplans])

	useEffect(() => {
		let asyncGet = async () => {
			let resp = await getGeo(props.data.property)
			setGeo(resp.geo)
		}
		asyncGet()
	}, [props.data])

	const validation = () => {
		// Skip validation for admin users
		if (props.isSuper) {
			return Yup.object().shape({})
		}

		let model = {
			floorplan: Yup.number("Start typing to choose a floorplan from the list.")
				.positive("Start typing to choose a floorplan from the list.")
				.required("Start typing to choose a floorplan from the list."),
			elevation: Yup.string().typeError("Required").required("Required"),
			brick: Yup.string().typeError("Required").required("Required"),
			stone: Yup.string().typeError("Required").required("Required"),
			trim: Yup.string().typeError("Required").required("Required"),
			siding: Yup.string().typeError("Required").required("Required"),
			stucco: Yup.string().typeError("Required").required("Required"),
			sqft: Yup.number().required("Required"),
			arc_options: Yup.string().typeError("Required").required("Required"),
			plan_file: Yup.mixed()
				.required("Required")
				.test("fileType", "Only PDF or ZIP files are allowed", (value) => {
					if (value instanceof File) {
						return ["application/pdf", "application/zip"].includes(value.type)
					}
					return false
				}),
		}
		//? High visibility masonry
		if (settings?.enabled?.highVisMasonry && props?.data?.is_featured) {
			model.masonry_percent = Yup.number()
				.required("Required")
				.min(
					settings?.enabled?.highVisMasonry,
					`Must be at least ${settings?.enabled?.highVisMasonry}% due to high visibility.`
				)
		}
		if (settings?.customValidation?.arcLot) {
			try {
				let shape = settings.customValidation.arcLot
				if (settings.arcValidateSQFTMax) {
					let lt = _.find(payload.public.lot_types, { id: props.data.lot_type })
					if (lt && lt.sqft_max) {
						shape.sqft = Yup.number().max(lt.sqft_max, `Max sqft is ${lt.sqft_max}.`).required("Required")
					}
				}
				if (props?.data?.arc_data?.plan_file) delete shape.plan_file
			} catch (e) {
				console.log(e)
			}
			return Yup.object().shape(settings.customValidation.arcLot)
		}
		if (
			settings.arcSitePlanFiles &&
			settings?.arcSitePlanFiles[props.data.property] &&
			settings.arcSitePlanFiles[props.data.property].length
		) {
			_.forEach(settings.arcSitePlanFiles[props.data.property], (v) => {
				if (v.required) {
					model[v.name] = Yup.mixed()
						.required("Required")
						.test("fileType", "Unsupported File Format", (value) => {
							return (
								value &&
								[
									"application/pdf",
									"image/jpeg",
									"image/png",
									"application/msword",
									"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
								].includes(value.type)
							)
						})
				}
			})
		}
		return Yup.object().shape(model)
	}

	const submitClasses = (isValid) => {
		if (blocking) {
			return "hidden"
		}
		return classnames("form__submit", {
			"form__submit--ready": isValid || props.isSuper,
		})
	}

	const getGeo = async (property) => {
		var user = localStorage.getItem("user")
		return new Promise((resolve, reject) => {
			axios
				.post(settings.ep + "/getGeo", { property: property }, { headers: { Authorization: `Bearer ${user}` } })
				.then((response) => {
					if (response.data.status == 200) {
						resolve(response.data)
					} else {
						let err = new Error()
						err.message = response.data.message
						err.status = response.data.status
						reject(err)
					}
				})
				.catch((error) => {
					reject(error)
				})
		})
	}

	const arcSitePlanFiles = (f) => {
		try {
			let property = props?.data?.property
			if (
				settings?.arcSitePlanFiles &&
				settings?.arcSitePlanFiles[property] &&
				settings.arcSitePlanFiles[property].length
			) {
				return settings.arcSitePlanFiles[property].map((v, i) => {
					return (
						<li className={fieldStatus(v.name, f)} key={`file-${v.name}`}>
							<label htmlFor={v.name}>
								{v.title} {v.required && <sup>*</sup>}
							</label>
							{f.errors[v.name] && f.touched[v.name] && (
								<div className="form__message">{f.errors[v.name]}</div>
							)}
							<input
								name={v.name}
								type="file"
								id={v.name}
								className={"noBorder"}
								onChange={(event) => {
									f.setFieldValue(v.name, event.currentTarget.files[0])
								}}
							/>
						</li>
					)
				})
			}
		} catch (e) {
			console.log(e)
		}
		return null
	}

	const customLotFields = (f) => {
		try {
			if (settings?.arcCustomLotFields) {
				let ret = []
				_.forEach(settings.arcCustomLotFields, (v, k) => {
					if (v.properties.includes("*") || v.properties.includes(props.data.property)) {
						let field
						if (v.type === "date") {
							field = (
								<li className={fieldStatus(v.dbName, f)} key={`custom-${v.dbName}`}>
									<label htmlFor={v.dbName}>
										{v.name} {v.required && <sup>*</sup>}
									</label>
									{f.errors[v.dbName] && f.touched[v.dbName] && (
										<div className="form__message">{f.errors[k]}</div>
									)}
									<DateField
										name={v.dbName}
										onChange={f.setFieldValue}
										onBlur={f.setFieldTouched}
										value={f.values[v.dbName]}
										error={f.errors[v.dbName]}
										touched={f.touched[v.dbName]}
									/>
								</li>
							)
							ret.push(field)
						} else if (v.type === "text") {
							field = (
								<li className={fieldStatus(v.dbName, f)} key={`custom-${v.dbName}`}>
									<label htmlFor={v.dbName}>
										{v.name} {v.required && <sup>*</sup>}
									</label>
									{f.errors[v.dbName] && f.touched[v.dbName] && (
										<div className="form__message">{f.errors[k]}</div>
									)}
									<Field
										name={v.dbName}
										type="text"
										className="form__input"
										onChange={(e) => {
											let val = e.target.value
											setState((prevState) => ({
												...prevState,
												current: { ...prevState.current, [v.dbName]: val },
											}))
											f.setFieldValue(v.dbName, val)
										}}
									/>
								</li>
							)
							ret.push(field)
						} else if (v.type === "select") {
							field = (
								<li className={fieldStatus(v.dbName, f)} key={`custom-${v.dbName}`}>
									<label htmlFor={v.dbName}>
										{v.name} {v.required && <sup>*</sup>}
									</label>
									{f.errors[v.dbName] && f.touched[v.dbName] && (
										<div className="form__message">{f.errors[v.dbName]}</div>
									)}
									<ReactSelect
										name={v.dbName}
										options={v.options}
										value={f.values[v.dbName]}
										onBlur={f.setFieldTouched}
										onChange={f.setFieldValue}
										clearable
									/>
								</li>
							)
							ret.push(field)
						}
					}
				})
				return ret
			}
		} catch (e) {
			console.log(e)
		}
	}

	const outputElevationDropdown = (f) => {
		let fp = _.find(payload.public.floorplans, { id: f.values.floorplan })
		if (!f.values.floorplan || !fp)
			return (
				<li className={fieldStatus("elevation", f)}>
					<label htmlFor="elevation">
						Elevation<sup>*</sup>
					</label>
				</li>
			)

		let elevations = _.filter(
			payload.public.elevations,
			(v) => v.floorplan === f.values.floorplan && v.property === props.data.property && v.approval_status === 1
		)
		elevations = _.sortBy(elevations, (v) => v.name)
		let opts = _.map(elevations, (v) => ({
			label: v.name,
			value: v.name,
		}))
		return (
			<li className={fieldStatus("elevation", f)}>
				<label htmlFor="elevation">
					Elevation<sup>*</sup>
				</label>
				{f.errors.elevation && f.touched.elevation && <div className="form__message">{f.errors.elevation}</div>}
				<ReactSelect
					name={`elevation`}
					options={opts}
					value={f.values.elevation}
					onBlur={f.setFieldTouched}
					onChange={(k, v) => {
						f.setFieldValue(k, v)
						setState((prevState) => ({
							...prevState,
							current: { ...prevState.current, elevation: v },
						}))
					}}
				/>
			</li>
		)
	}

	const formLayout = (f) => {
		let builder = _.get(state, `user.builders`)
		const getMaterialField = (name, typeId, f) => {
			let material = _.find(payload.public.arc_materials, { type: typeId })
			let colors = helpers.arcColorTypes()
			let opts
			if (colors.includes(name) && props.payload?.public?.arc_colors?.length > 0) {
				opts = helpers.selectARCColors(props.payload.public.arc_colors)
			} else if (material) {
				opts = payload.public.arc_materials
					.filter((v) => v.type === typeId && v.status !== 0)
					.map((v) => ({
						label: v.brand ? `${v.brand} - ${v.name}` : v.name,
						value: v.id,
					}))
					.sort((a, b) => a.label.localeCompare(b.label))
			}
			if (opts) {
				try {
					//? This try block looks at the acceptable materials for each classification/style.  So for exapmle COLTON Prairie allows certain types of brick, siding etc.  This allows them to limit that.  If there isn't any materials assigned to that style, then this is just skipped.
					if (f.values.floorplan) {
						let fp = _.find(payload.public.floorplans, { id: f.values.floorplan })
						if (fp?.classifications && fp.classifications?.length) {
							let classifications = []
							_.forEach(fp.classifications, (v) => {
								let c = _.find(payload.public.floorplan_classifications, { id: v })
								if (c) {
									classifications.push(c)
								}
							})
							// We have this plans classifcations now, now compile a list of allowed materials
							let allowed = []
							_.forEach(classifications, (v) => {
								if (v[name]) {
									allowed.push(...v[name])
								}
							})
							if (allowed.length) {
								opts = opts.filter((v) => {
									return allowed.includes(v.value)
								})
							}
						}
					}
				} catch (e) {
					console.log(e)
				}
				return (
					<ReactSelect
						name={name}
						options={opts}
						value={f.values[name]}
						onBlur={f.setFieldTouched}
						onChange={(k, v) => {
							f.setFieldValue(k, v)
							setState((prevState) => ({
								...prevState,
								current: { ...prevState.current, [k]: v },
							}))
						}}
						clearable
					/>
				)
			}

			return (
				<Field
					name={name}
					type="text"
					className="form__input"
					onChange={(e) => {
						let val = e.target.value
						setState((prevState) => ({
							...prevState,
							current: { ...prevState.current, [name]: val },
						}))
						f.setFieldValue(name, val)
					}}
				/>
			)
		}
		outputElevationDropdown(f)
		return (
			<div>
				<div className="flex justify-between">
					<span
						className="text-blue text-xs cursor-pointer"
						onClick={async () => {
							let c = _.cloneDeep(f.values)
							c.action = "saveARCLotProgress"
							c.id = props.data.id
							let resp = await submitDataAsync("mainIngest", c)
							if (resp.status === 200) {
								toast.success("Progress Saved.  When complete, please submit for review.")
							}
						}}>
						<i className="fa-solid fa-download mr-1"></i>Save Progress
					</span>
					<Delete />
				</div>
				<Form>
					<ul className="modalForm__list">
						<li className={fieldStatus("floorplan", f)}>
							<label htmlFor="floorplan">
								Floorplan <sup>*</sup>
							</label>
							{f.errors.floorplan && f.touched.floorplan && (
								<div className="form__message">{f.errors.floorplan}</div>
							)}
							<Typeahead
								name="floorplan"
								className="form__input"
								value={f.values.floorplan}
								onChange={(k, v) => {
									setState((prevState) => ({
										...prevState,
										current: { ...prevState.current, floorplan: v },
									}))
									f.setFieldValue("floorplan", v)
								}}
								onBlur={f.setFieldTouched}
								touched={f.touched.floorplan}
								error={f.errors.floorplan}
								placeholder={f.values.floorplan_label}
								ep="/typeaheadFloorplans"
								extra={{ property: props.data.property, builder, status: 1 }}
							/>
						</li>
						{settings?.enabled?.arcLimitElevations ? (
							outputElevationDropdown(f)
						) : (
							<li className={fieldStatus("elevation", f)}>
								<label htmlFor="elevation">
									Elevation<sup>*</sup>
								</label>
								{f.errors.elevation && f.touched.elevation && (
									<div className="form__message">{f.errors.elevation}</div>
								)}
								<Field
									name="elevation"
									type="text"
									className="form__input"
									onBlur={(v) => {
										let val = v.target.value
										f.setFieldTouched("elevation")
										setState((prevState) => ({
											...prevState,
											current: { ...prevState.current, elevation: val },
										}))
									}}
								/>
							</li>
						)}
						<li className={fieldStatus("brick", f)}>
							<label htmlFor="brick">Brick Color</label>
							{f.errors.brick && f.touched.brick && <div className="form__message">{f.errors.brick}</div>}
							{getMaterialField("brick", 1, f)}
						</li>
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("brick_material") ? null : (
							<li className={fieldStatus("brick_material", f)}>
								<label htmlFor="brick">Brick Material</label>
								{f.errors.brick_material && f.touched.brick_material && (
									<div className="form__message">{f.errors.brick_material}</div>
								)}
								{getMaterialField("brick_material", 1, f)}
							</li>
						)}
						<li className={fieldStatus("stone", f)}>
							<label htmlFor="stone">Stone</label>
							{f.errors.stone && f.touched.stone && <div className="form__message">{f.errors.stone}</div>}
							{getMaterialField("stone", 4, f)}
						</li>
						<li className={fieldStatus("trim", f)}>
							<label htmlFor="trim">Trim</label>
							{f.errors.trim && f.touched.trim && <div className="form__message">{f.errors.trim}</div>}
							{getMaterialField("trim", 5, f)}
						</li>
						<li className={fieldStatus("siding", f)}>
							<label htmlFor="siding">Siding Color</label>
							{f.errors.siding && f.touched.siding && (
								<div className="form__message">{f.errors.siding}</div>
							)}
							{getMaterialField("siding", 11, f)}
						</li>
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("siding_type") ? null : (
							<li className={fieldStatus("siding_type", f)}>
								<label htmlFor="siding_type">Siding Type</label>
								{f.errors.siding_type && f.touched.siding_type && (
									<div className="form__message">{f.errors.siding_type}</div>
								)}
								{getMaterialField("siding_type", 3, f)}
							</li>
						)}
						<li className={fieldStatus("stucco", f)}>
							<label htmlFor="stucco">Stucco Color</label>
							{f.errors.stucco && f.touched.stucco && (
								<div className="form__message">{f.errors.stucco}</div>
							)}
							{getMaterialField("stucco", 2, f)}
						</li>
						<li className={fieldStatus("accent_color", f)}>
							<label htmlFor="accent_color">{settings?.renames?.accent_color || "Accent Color"}</label>
							{f.errors.accent_color && f.touched.accent_color && (
								<div className="form__message">{f.errors.accent_color}</div>
							)}
							{getMaterialField("accent_color", 6, f)}
						</li>
						<li className={fieldStatus("garage_color", f)}>
							<label htmlFor="garage_color">Garage Color</label>
							{f.errors.garage_color && f.touched.garage_color && (
								<div className="form__message">{f.errors.garage_color}</div>
							)}
							{getMaterialField("garage_color", 7, f)}
						</li>
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("door_color") ? null : (
							<li className={fieldStatus("door_color", f)}>
								<label htmlFor="door_color">Door Color</label>
								{f.errors.door_color && f.touched.door_color && (
									<div className="form__message">{f.errors.door_color}</div>
								)}
								{getMaterialField("door_color", 10, f)}
							</li>
						)}
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("roof_color") ? null : (
							<li className={fieldStatus("roof_color", f)}>
								<label htmlFor="roof_color">Roof Color</label>
								{f.errors.roof_color && f.touched.roof_color && (
									<div className="form__message">{f.errors.roof_color}</div>
								)}
								{getMaterialField("roof_color", 8, f)}
							</li>
						)}
						<li className={fieldStatus("sqft", f)}>
							<label htmlFor="sqft">
								Address Specific Sq. Ft.<sup>*</sup>
								<p className="text-[10px] mt-1">Total sqft of this plan as submitted.</p>
							</label>
							{f.errors.sqft && f.touched.sqft && <div className="form__message">{f.errors.sqft}</div>}
							<NumberField
								name="sqft"
								type="text"
								className="form__input"
								onChange={f.setFieldValue}
								onBlur={f.setFieldTouched}
								touched={f.touched.sqft}
								error={f.errors.sqft}
								value={f.values.sqft}
								thousandSeparator={true}
							/>
						</li>
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("masonry_percent") ? null : (
							<li className={fieldStatus("masonry_percent", f)}>
								<label htmlFor="masonry_percent">Masonry %</label>
								{f.errors.masonry_percent && f.touched.masonry_percent && (
									<div className="form__message">{f.errors.masonry_percent}</div>
								)}
								<NumberField
									name="masonry_percent"
									type="text"
									className="form__input"
									onChange={f.setFieldValue}
									onBlur={f.setFieldTouched}
									touched={f.touched.masonry_percent}
									error={f.errors.masonry_percent}
									value={f.values.masonry_percent}
									thousandSeparator={true}
								/>
							</li>
						)}
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("roof_pitch") ? null : (
							<li className={fieldStatus("roof_pitch", f)}>
								<label htmlFor="roof_pitch">Roof Pitch</label>
								{f.errors.roof_pitch && f.touched.roof_pitch && (
									<div className="form__message">{f.errors.roof_pitch}</div>
								)}
								<Field
									name="roof_pitch"
									type="text"
									className="form__input"
									onBlur={(v) => {
										let val = v.target.value
										f.setFieldTouched("roof_pitch")
										setState((prevState) => ({
											...prevState,
											current: { ...prevState.current, roof_pitch: val },
										}))
									}}
								/>
							</li>
						)}
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("total_height") ? null : (
							<li className={fieldStatus("total_height", f)}>
								<label htmlFor="total_height">Total Height</label>
								{f.errors.total_height && f.touched.total_height && (
									<div className="form__message">{f.errors.total_height}</div>
								)}
								<Field
									name="total_height"
									type="text"
									className="form__input"
									onBlur={(v) => {
										let val = v.target.value
										f.setFieldTouched("total_height")
										setState((prevState) => ({
											...prevState,
											current: { ...prevState.current, total_height: val },
										}))
									}}
								/>
							</li>
						)}
						{settings?.arcLotFieldHides?.[props.data.property]?.includes("driveway_position") ? null : (
							<li className={fieldStatus("driveway_position", f, false, false, false)}>
								<label htmlFor="driveway_position">Driveway Position</label>
								{f.errors.driveway_position && f.touched.driveway_position && (
									<div className="form__message">{f.errors.driveway_position}</div>
								)}
								<ReactSelect
									name={`driveway_position`}
									options={[
										{
											label: "Left",
											value: "Left",
										},
										{
											label: "Right",
											value: "Right",
										},
									]}
									value={f.values.driveway_position}
									onBlur={f.setFieldTouched}
									onChange={f.setFieldValue}
								/>
							</li>
						)}
						{customLotFields(f)}
						<li className={fieldStatus("plan_file", f)}>
							<label htmlFor="plan_file">
								Plan File{" "}
								{props.data?.arc_data?.plan_file && (
									<a
										href={`${settings.s3}${props.data?.arc_data?.plan_file}`}
										className="btn !inline !text-xs ml-3"
										target="_blank">
										View Current File
									</a>
								)}
							</label>
							<p className="text-xs mb-1">
								Please combine all documents into one PDF or ZIP file for review.
							</p>
							{f.errors.plan_file && f.touched.plan_file && (
								<div className="form__message">{f.errors.plan_file}</div>
							)}
							<input
								name="plan_file"
								type="file"
								id="plan_file"
								className={"noBorder"}
								//className="noBorder fileHideText"
								onChange={(event) => {
									f.setFieldValue("plan_file", event.currentTarget.files[0])
								}}
							/>
						</li>
						{arcSitePlanFiles(f)}
						<li className={fieldStatus("comments", f, null, null, true)}>
							<label htmlFor="comments">Comments/Notes</label>
							{f.errors.comments && f.touched.comments && (
								<div className="form__message">{f.errors.comments}</div>
							)}
							<TextArea
								name="comments"
								className="form__textarea"
								value={f.values.comments}
								onChange={f.setFieldValue}
								onBlur={f.setFieldTouched}
								touched={f.touched.comments}
								error={f.errors.comments}
							/>
						</li>
						<li className={fieldStatus("variance", f, null, null, true)}>
							<label htmlFor="variance">
								Variance <span className="text-xs">(If applicable)</span>
							</label>
							{f.errors.variance && f.touched.variance && (
								<div className="form__message">{f.errors.variance}</div>
							)}
							<TextArea
								name="variance"
								className="form__textarea"
								value={f.values.variance}
								onChange={f.setFieldValue}
								onBlur={f.setFieldTouched}
								touched={f.touched.variance}
								error={f.errors.variance}
							/>
						</li>
						<li className={fieldStatus("arc_options", f, null, null, true)}>
							<label htmlFor="arc_options">
								Options <sup>*</sup>
							</label>
							{f.errors.arc_options && f.touched.arc_options && (
								<div className="form__message">{f.errors.arc_options}</div>
							)}
							<TextArea
								name="arc_options"
								className="form__textarea"
								value={f.values.arc_options}
								onChange={f.setFieldValue}
								onBlur={f.setFieldTouched}
								touched={f.touched.arc_options}
								error={f.errors.arc_options}
							/>
						</li>
						{props.super && (
							<li className={fieldStatus("approval_comments", f, null, null, true)}>
								<label htmlFor="approval_comments">Approval Comments</label>
								{f.errors.approval_comments && f.touched.approval_comments && (
									<div className="form__message">{f.errors.approval_comments}</div>
								)}
								<TextArea
									name="approval_comments"
									className="form__textarea"
									value={f.values.approval_comments}
									onChange={f.setFieldValue}
									onBlur={f.setFieldTouched}
									touched={f.touched.approval_comments}
									error={f.errors.approval_comments}
								/>
							</li>
						)}
					</ul>
					<input type="submit" value="Submit" className={submitClasses(f.isValid)} />
				</Form>
			</div>
		)
	}

	const Tags = () => {
		let ret = []
		if (props?.data?.is_featured) {
			ret.push(<span className="px-2 py-1 text-white text-sm bg-red-500 rounded-md">High Visibility</span>)
		}

		return ret
	}

	const Delete = () => {
		if (props.data?.arc_data) {
			return (
				<div className="mb-3">
					<span
						className="bg-red-500 text-white px-2 py-1 rounded-md cursor-pointer hover:bg-red-600 text-xs font-bold"
						onClick={async () => {
							if (confirmDelete) {
								dispatch(loadingStatus(true))
								await submitDataAsync("mainIngest", {
									id: props.data.id,
									action: "arcDeleteLotSubmission",
								})
									.then((data) => {
										dispatch(loadingStatus(false))
										setConfirmDelete(false)
										props.notify(data)
									})
									.catch((e) => {
										props.notify(e.message)
									})
							} else {
								setConfirmDelete(true)
							}
						}}>
						{confirmDelete ? (
							<span>Are you sure? Click again to confirm.</span>
						) : (
							<span>Delete ARC Submission</span>
						)}
					</span>
				</div>
			)
		}
		return null
	}

	return (
		<div className="modal">
			<div className="flex">
				<div className="flex-1 flex">
					<div className="flex-1 pr-3">
						<h3>Submit Plan for Approval: {props.data.address}</h3>
						<Tags />
						{state.error ? (
							<div className="bg-red-400 text-white px-2 py-1 border-dashed border-white border text-sm font-bold">
								{state.error}
							</div>
						) : null}
						<Formik
							initialValues={state.fields}
							validationSchema={validation}
							enableReinitialize
							onSubmit={(values) => {
								values.id = props.data.id
								values.super = props.isSuper ? 1 : 0
								let json = JSON.stringify(values)
								dispatch(loadingStatus(true))
								var fd = new FormData()
								for (let k in values) {
									fd.append(k, values[k])
								}
								var user = localStorage.getItem("user")
								let req = superagent
									.post(settings.ep + "/inputARC")
									.field("data", json)
									.set("Authorization", user)
								// Add on the files
								_.forEach(values, (v, k) => {
									if (v instanceof File) {
										req.attach(k, v)
									}
								})
								req.end((err, resp) => {
									if (err) {
										props.notify(err, name)
										return
									}
									props.notify(resp.body, name)
									dispatch(loadingStatus(false))
								})
							}}
							render={(f) => formLayout(f)}
						/>
					</div>
				</div>
				<div className="flex-2 pl-2">
					<ArcRepetitionMap
						id={props.data?.id}
						submit={true}
						data={state.current}
						interface={"entry"}
						setBlocking={setBlocking}
					/>
				</div>
			</div>
		</div>
	)
}

export default ArcModal
